/**
 * CLI Parser Types
 *
 * Type definitions for CLI command parsing.
 */

import type { ParsedMcpServer } from '../types';

/**
 * Supported CLI tool identifiers
 */
export type CliTool = 'claude' | 'gemini' | 'codex';

/**
 * Options passed to a CLI parser
 */
export interface CliParseOptions {
  /** The tokenized command (array of strings) */
  tokens: string[];
  /** Original raw command string (for error messages) */
  rawCommand: string;
}

/**
 * Result of parsing a CLI command
 */
export interface CliParseResult {
  /** Whether parsing succeeded */
  success: boolean;
  /** Parsed server configurations */
  servers: ParsedMcpServer[];
  /** Error message if parsing failed */
  error?: string;
  /** Non-fatal warnings */
  warnings?: string[];
  /** The tool that parsed this command */
  tool?: CliTool;
}

/**
 * Intermediate parsed data from CLI flags
 */
export interface ParsedCliFlags {
  /** Transport type (stdio, http, sse) */
  transport?: 'stdio' | 'http' | 'sse';
  /** Environment variables */
  env: Record<string, string>;
  /** HTTP headers */
  headers: Record<string, string>;
  /** Server name */
  name?: string;
  /** URL for http/sse transports */
  url?: string;
  /** Command for stdio transport */
  command?: string;
  /** Arguments for the command */
  args: string[];
  /** Scope (user, project, etc.) - informational only */
  scope?: string;
  /** Timeout in milliseconds */
  timeout?: number;
  /** Description */
  description?: string;
  /** Tools to include */
  includeTools?: string[];
  /** Tools to exclude */
  excludeTools?: string[];
  /** Any unrecognized flags */
  unknownFlags: string[];
}

/**
 * Factory function for creating an empty ParsedCliFlags
 */
export const createEmptyParsedFlags = (): ParsedCliFlags => ({
  env: {},
  headers: {},
  args: [],
  unknownFlags: [],
});

/**
 * Interface for CLI parser implementations
 */
export interface ICliParser {
  /** The CLI tool this parser handles */
  readonly tool: CliTool;
  /** Human-readable name of the tool */
  readonly displayName: string;
  /** Command prefix(es) this parser handles */
  readonly prefixes: string[];

  /**
   * Check if this parser can handle the given tokens
   */
  canHandle(tokens: string[]): boolean;

  /**
   * Parse the CLI command into MCP server configuration(s)
   */
  parse(options: CliParseOptions): CliParseResult;
}
